import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import GridLayout, { type Layout } from 'react-grid-layout';
import { LayoutGrid } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/AuthProvider';
import { useAppStore } from '@/app/appStore';
import { useResolvedTheme } from '@/app/theme';
import { useChatStore } from '@/chat/chatStore';
import { createBus } from '@/panels/detachBus';
import { clearDetachedBootstrap, resolveDetachSessionId } from '@/panels/detachedState';
import { usePanelStore } from '@/panels/panelStore';
import { PanelShell } from '@/panels/PanelShell';
import { MobileWorkspace } from '@/panels/MobileWorkspace';
import { useMobileWorkspaceStore } from '@/panels/mobileWorkspaceStore';
import { useIsMobile } from '@/shared/useIsMobile';
import { BASE_GRID_COLS, getWorkspaceGridCols, scaleLayoutCols } from '@/panels/grid';
import { SaveTemplateDialog, type SaveTemplatePayload } from '@/templates/SaveTemplateDialog';
import 'react-grid-layout/css/styles.css';

const ROW_HEIGHT = 40;

export function Workspace() {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const sessionId = useChatStore((s) => s.sessionId);
  const panels = usePanelStore((s) => s.panels);
  const closePanel = usePanelStore((s) => s.closePanel);
  const focusedPanelId = usePanelStore((s) => s.focusedPanelId);
  const patchPanelPayload = usePanelStore((s) => s.patchPanelPayload);
  const setPanelLayout = usePanelStore((s) => s.setPanelLayout);
  const replacePanel = usePanelStore((s) => s.replacePanel);
  const restorePanel = usePanelStore((s) => s.restorePanel);
  const pushPanel = useMobileWorkspaceStore((s) => s.pushPanel);
  const clearStack = useMobileWorkspaceStore((s) => s.clearStack);
  const [saveTarget, setSaveTarget] = useState<SaveTemplatePayload | null>(null);
  const [dragging, setDragging] = useState(false);
  const [gridWidth, setGridWidth] = useState(1200);
  const panelsRef = useRef(panels);
  const gridObserverRef = useRef<ResizeObserver | null>(null);

  const gridContainerRef = useCallback((el: HTMLDivElement | null) => {
    // Disconnect any previous observer when the element unmounts or swaps.
    if (gridObserverRef.current) {
      gridObserverRef.current.disconnect();
      gridObserverRef.current = null;
    }
    if (!el || typeof ResizeObserver === 'undefined') return;

    const measure = (width: number) => {
      const next = Math.max(320, Math.floor(width));
      if (next > 0) setGridWidth(next);
    };

    // Measure immediately on mount.
    measure(el.clientWidth);

    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width ?? el.clientWidth;
      measure(w);
    });
    observer.observe(el);
    gridObserverRef.current = observer;
  }, []);

  const setCommandPaletteOpen = useAppStore((s) => s.setCommandPaletteOpen);
  const themePreference = useAppStore((s) => s.theme);
  const leftNavCollapsed = useAppStore((s) => s.leftNavCollapsed);
  const chatCollapsed = useAppStore((s) => s.chatCollapsed);
  const chatPosition = useAppStore((s) => s.chatPosition);
  const resolvedTheme = useResolvedTheme(themePreference);
  const cols = getWorkspaceGridCols({ leftNavCollapsed, chatCollapsed, chatPosition });

  useEffect(() => {
    panelsRef.current = panels;
  }, [panels]);

  // On mobile, push newly opened panels onto the mobile stack so they become visible.
  const prevPanelCountRef = useRef(0);
  useEffect(() => {
    if (!isMobile) return;
    const nonDetached = panels.filter((p) => !p.detached);
    if (nonDetached.length > prevPanelCountRef.current) {
      const newest = nonDetached[nonDetached.length - 1];
      if (newest) pushPanel(newest.panelId);
    }
    prevPanelCountRef.current = nonDetached.length;
  }, [isMobile, panels, pushPanel]);

  // When the viewport transitions to mobile with existing panels, rebuild the stack
  // so every non-detached panel is reachable. Without this, only panels opened
  // after the transition are tracked.
  const prevIsMobileRef = useRef(isMobile);
  useEffect(() => {
    if (!isMobile || prevIsMobileRef.current === isMobile) {
      prevIsMobileRef.current = isMobile;
      return;
    }
    prevIsMobileRef.current = isMobile;
    clearStack();
    const nonDetached = panels.filter((p) => !p.detached);
    prevPanelCountRef.current = nonDetached.length;
    for (const p of nonDetached) {
      pushPanel(p.panelId);
    }
  }, [isMobile, panels, clearStack, pushPanel]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    const bus = createBus(resolveDetachSessionId(sessionId, user?.sub));
    const unsubscribe = bus.subscribe((msg) => {
      if (msg.kind === 'panel_opened' || msg.kind === 'panel_payload_patch') {
        const data = msg.data as { panel?: unknown } | undefined;
        const panel = data?.panel;
        if (panel && typeof panel === 'object' && 'panelId' in panel) {
          replacePanel(panel as Parameters<typeof replacePanel>[0]);
        }
        return;
      }

      if (msg.kind === 'panel_closed') {
        const data = msg.data as { updatedAt?: number } | undefined;
        clearDetachedBootstrap(msg.panelId);
        restorePanel(msg.panelId, data?.updatedAt ?? msg.ts);
      }
    });

    const publishSnapshots = () => {
      for (const panel of panelsRef.current) {
        if (!panel.detached) continue;
        bus.publish({
          kind: 'panel_opened',
          panelId: panel.panelId,
          data: { panel },
        });
      }
    };

    window.addEventListener('focus', publishSnapshots);

    return () => {
      window.removeEventListener('focus', publishSnapshots);
      unsubscribe();
      bus.close();
    };
  }, [replacePanel, restorePanel, sessionId, user?.sub]);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') return;

    const bus = createBus(resolveDetachSessionId(sessionId, user?.sub));

    for (const panel of panels) {
      if (!panel.detached) continue;
      bus.publish({
        kind: 'theme_changed',
        panelId: panel.panelId,
        data: { theme: resolvedTheme },
      });
    }

    bus.close();
  }, [panels, resolvedTheme, sessionId, user?.sub]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (!focusedPanelId) return;
      const active = document.activeElement;
      if (
        active instanceof HTMLInputElement ||
        active instanceof HTMLTextAreaElement ||
        active instanceof HTMLSelectElement ||
        (active instanceof HTMLElement && active.getAttribute('role') === 'dialog')
      ) return;
      closePanel(focusedPanelId);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [focusedPanelId, closePanel]);

  const layout: Layout = useMemo(
    () =>
      panels
        .filter((p) => !p.detached)
        .map((p) => ({
          i: p.panelId,
          ...scaleLayoutCols(p.layout, p.layoutCols ?? BASE_GRID_COLS, cols),
          minW: 3,
          minH: 4,
        })),
    [cols, panels],
  );

  // Mobile: delegate entirely to MobileWorkspace (handles its own empty state)
  if (isMobile) {
    return <MobileWorkspace />;
  }

  if (panels.length === 0) {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-muted-foreground select-none">
        <LayoutGrid className="h-10 w-10 opacity-20" />
        <p className="text-sm">{t('workspace.empty')}</p>
        <button
          type="button"
          onClick={() => setCommandPaletteOpen(true)}
          className="mt-1 rounded-md border border-border px-3 py-1.5 text-xs hover:bg-muted hover:text-foreground transition-colors"
        >
          {t('workspace.browseSurfaces')}
        </button>
      </div>
    );
  }

  return (
    <div
      ref={gridContainerRef}
      data-testid="workspace-grid-container"
      data-workspace-cols={cols}
      className="bg-background relative h-full flex-1 overflow-auto"
    >
      {dragging && (
        <div
          className="pointer-events-none absolute inset-0 z-0 grid"
          style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 8 }}
        >
          {Array.from({ length: cols }).map((_, i) => (
            <div key={i} className="h-full border-l border-border/20" />
          ))}
        </div>
      )}
      <GridLayout
        className="layout"
        layout={layout}
        gridConfig={{ cols, rowHeight: ROW_HEIGHT }}
        width={gridWidth}
        dragConfig={{ handle: '.panel-drag-handle' }}
        onDragStart={() => setDragging(true)}
        onDragStop={() => setDragging(false)}
        onLayoutChange={(nextLayouts) => {
          const updatedAt = Date.now();
          for (const item of nextLayouts) {
            setPanelLayout(
              item.i,
              {
                x: item.x,
                y: item.y,
                w: item.w,
                h: item.h,
              },
              updatedAt,
              cols,
            );
          }
        }}
      >
        {panels
          .filter((panel) => !panel.detached)
          .map((panel) => (
            <div key={panel.panelId} data-panel-id={panel.panelId}>
              <PanelShell
                panel={panel}
                onClose={closePanel}
                onPatchPayload={patchPanelPayload}
                onSaveTemplate={(cfg) => {
                  const c = cfg as { surface: string; payload: unknown };
                  setSaveTarget({ surface: c.surface, config: c.payload });
                }}
              />
            </div>
          ))}
      </GridLayout>
      <SaveTemplateDialog
        open={saveTarget !== null}
        onOpenChange={(o) => {
          if (!o) setSaveTarget(null);
        }}
        payload={saveTarget}
      />
    </div>
  );
}
